import type {
  ConsoleEntry,
  OutputModalOptions,
  ValidationField,
} from '../../command_center/components/center/modals/console/types/consoleTypes';
import type { TestPlan } from '../types/validateDetails.types';
import type { SessionResultItem, SessionResultStatus } from '../api/validate.api';
import { get_formated_time } from '../../command_center/utils';
import {
  TcTmCombinedGreenIcon,
  TcIconGreen,
  TmIconGreen,
} from '../../command_center/components/center/modals/Svgs';
import {
  BeaconSvg,
  HealthMetricSvg,
  InvalidTmSvg,
} from '../../command_center/components/center/modals/console/ConsoleSvg';
import {
  SECTION_HEADER_HEIGHT,
  ROW_HEIGHT,
  SECTION_BORDER_HEIGHT,
  SECTION_GAP,
} from '../constants/reports.constants';
import type { FlatRow, GroupedSection, RowResultState } from '../types/reports.types';

// ─── Output tab state ─────────────────────────────────────────────────────────

export function hasValidationData(entry: ConsoleEntry | null): boolean {
  if (!entry) return false;
  const rawDetails = entry.ValidationDetails ?? entry.CommandDetails?.ValidationDetails;
  const rawInfo = entry.ValidationInfo ?? entry.CommandDetails?.ValidationInfo;
  const hasArrayData = (Array.isArray(rawDetails) && rawDetails.length > 0) || (Array.isArray(rawInfo) && rawInfo.length > 0);
  return hasArrayData || entry.result !== undefined;
}

export function getDefaultOutputTabState(entry: ConsoleEntry | null): OutputModalOptions {
  const entryType = String(entry?.type ?? entry?.CommandDetails?.tctmType ?? '').trim().toLowerCase();
  const isTmEntry = entryType === 'tm';
  const shouldOpenValidations = isTmEntry && hasValidationData(entry);

  return {
    contents: false,
    json: !shouldOpenValidations,
    bytes: false,
    messages: false,
    validations: shouldOpenValidations,
  };
}

// ─── Plan / case selection IDs ────────────────────────────────────────────────

export function getPlanSelectionId(plan: TestPlan): string {
  return String(plan.testPlanResultId ?? plan.testPlanId);
}

export function getCaseSelectionId(testCase: TestPlan['testCases'][number]): string {
  return String(testCase.testCaseResultId ?? testCase.testCaseId);
}

export function planMatchesSelectionId(plan: TestPlan, selectionId: string | null): boolean {
  if (!selectionId) return false;
  const planSelId = getPlanSelectionId(plan);
  if (planSelId === selectionId || plan.testPlanId === selectionId || plan.testPlanResultId === selectionId) return true;
  return (plan.testCases ?? []).some(
    (testCase) =>
      getCaseSelectionId(testCase) === selectionId ||
      testCase.testCaseId === selectionId ||
      testCase.testCaseResultId === selectionId
  );
}

export function getEntryPlanSelectionId(entry: ConsoleEntry): string {
  return String(
    entry.testPlanResultId
    ?? (entry as { testPlanId?: string }).testPlanId
    ?? entry.testPlan
    ?? entry.testPlanName
    ?? ''
  ).trim();
}

export function getEntryCaseSelectionId(entry: ConsoleEntry): string {
  return String(entry.testCaseResultId ?? entry.testCaseId ?? entry.testCaseName ?? '').trim();
}

// ─── Group key helpers ────────────────────────────────────────────────────────

export function hasReportsGroupMetadata(value: {
  testPlanResultId?: string;
  testCaseResultId?: string;
  testPlanName?: string;
  testCaseName?: string;
}): boolean {
  const testCaseResultId = String(value.testCaseResultId ?? '').trim();
  const testCaseName = String(value.testCaseName ?? '').trim();
  const testPlanResultId = String(value.testPlanResultId ?? '').trim();
  const testPlanName = String(value.testPlanName ?? '').trim();

  const hasCaseMeta = testCaseResultId !== '' && testCaseName !== '';
  const hasPlanMeta = testPlanResultId !== '' && testPlanName !== '';
  const hasPartialPlanMeta = testPlanResultId !== '' || testPlanName !== '';

  return hasCaseMeta && (!hasPartialPlanMeta || hasPlanMeta);
}

export function getReportsGroupKey(value: {
  testPlanResultId?: string;
  testCaseResultId?: string;
  testPlanName?: string;
  testCaseName?: string;
}): string {
  if (!hasReportsGroupMetadata(value)) return '';

  return [
    value.testPlanResultId ?? '',
    value.testPlanName ?? '',
    value.testCaseResultId ?? '',
    value.testCaseName ?? '',
  ].join('::');
}

export function getReportsGroupTitle(value: {
  testPlanName?: string;
  testCaseName?: string;
  testCaseResultId?: string;
  testCaseId?: string;
}): string {
  const caseLabel = value.testCaseName ?? value.testCaseResultId ?? value.testCaseId ?? '—';
  return value.testPlanName ? `${value.testPlanName} | ${caseLabel}` : caseLabel;
}

// ─── Session result helpers ───────────────────────────────────────────────────

export function getSessionResultSelectionId(item: SessionResultItem): string {
  if (item.resultType === 'TestCase') {
    return String(item.testCaseResultId ?? '').trim();
  }
  return String(item.testPlanResultId ?? '').trim();
}

export function getStatusIconColor(status: SessionResultStatus | null): string {
  if (status === 'PASS') return '#CCF54E';
  if (status === 'FAIL') return '#ff3333';
  if (status === 'TIMEOUT' || status === 'CANCELLED') return '#FFA940';
  return '#f59e0b';
}

// ─── Validation helpers ───────────────────────────────────────────────────────

export function normalizeValidationInfo(value: unknown): ValidationField[] {
  if (!Array.isArray(value)) return [];

  return value.reduce<ValidationField[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const entry = item as Record<string, unknown>;
    acc.push({
      Parameter: entry.Parameter != null ? String(entry.Parameter) : undefined,
      type: entry.type != null ? String(entry.type) : undefined,
      Validations: entry.Validations != null ? String(entry.Validations) : undefined,
      ResultValue: entry.ResultValue != null ? String(entry.ResultValue) : undefined,
      ResultStatus: entry.ResultStatus != null ? String(entry.ResultStatus) : undefined,
      children: normalizeValidationInfo(entry.children),
    });
    return acc;
  }, []);
}

export function getValidationStatusTone(status: string | undefined): 'pass' | 'fail' | 'timeout' | 'cancelled' {
  const normalizedStatus = String(status ?? '').trim().toLowerCase();
  if (normalizedStatus === 'fail' || normalizedStatus === 'failed') return 'fail';
  if (normalizedStatus === 'timeout' || normalizedStatus === 'timed_out' || normalizedStatus === 'timedout') return 'timeout';
  if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled' || normalizedStatus.includes('abort')) return 'cancelled';
  if (normalizedStatus === 'pass' || normalizedStatus === 'passed' || normalizedStatus === 'success') return 'pass';
  return 'pass';
}

// ─── Date / time formatting ───────────────────────────────────────────────────

export function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} at ${hh}:${min}`;
  } catch {
    return iso;
  }
}

export function formatEpochSeconds(epochSeconds: number | undefined): string | undefined {
  if (typeof epochSeconds !== 'number' || !Number.isFinite(epochSeconds) || epochSeconds <= 0) {
    return undefined;
  }
  const epochMs = epochSeconds > 100000000000 ? epochSeconds : epochSeconds * 1000;
  return formatDate(new Date(epochMs).toISOString());
}

// ─── Row result helpers ───────────────────────────────────────────────────────

export function normalizeResult(rawResult: unknown): RowResultState {
  if (rawResult === true) return 'pass';
  if (rawResult === false) return 'fail';

  if (typeof rawResult === 'string') {
    const value = rawResult.trim().toUpperCase();
    if (value === 'PASS' || value === 'PASSED' || value === 'SUCCESS' || value === 'TRUE') return 'pass';
    if (value === 'FAIL' || value === 'FAILED' || value === 'FALSE') return 'fail';
    if (value === 'TIMEOUT' || value === 'TIMED_OUT' || value === 'TIMEDOUT') return 'timeout';
  }

  return 'pending';
}

export function toRowResult(entry: ConsoleEntry): RowResultState {
  return normalizeResult(entry.result);
}

export function getTimestampLabel(entry: ConsoleEntry): string | undefined {
  const commandDetails = entry.CommandDetails ?? {};
  const timestamp =
    typeof commandDetails.timestamp === 'number'
      ? commandDetails.timestamp
      : typeof commandDetails.groundTimestamp === 'number'
        ? Math.floor(commandDetails.groundTimestamp / 1000)
        : undefined;

  return timestamp != null ? get_formated_time(timestamp) : undefined;
}

// ─── TC/TM icon ───────────────────────────────────────────────────────────────

export function getTcTmIcon(tctmType: string | undefined, direction: FlatRow['direction']) {
  switch ((tctmType ?? '').trim()) {
    case 'Health Metric': return <HealthMetricSvg color="#7AB3FF" />;
    case 'Beacon':        return <BeaconSvg color="#7AB3FF" />;
    case 'Invalid TM':    return <InvalidTmSvg color="#C91818" />;
    case 'TM':
    case 'tm':            return <TmIconGreen />;
    case 'TC':
    case 'tc':            return <TcIconGreen />;
    case 'TCTM':          return <TcTmCombinedGreenIcon />;
    default:              return direction === 'down' ? <TmIconGreen /> : <TcIconGreen />;
  }
}

// ─── Cell class helper ────────────────────────────────────────────────────────

export function cellCls(variant: string, active: boolean): string {
  return `rp__cell rp__cell--${variant}${active ? ' rp__row--active' : ''}`;
}

// ─── Entry → flat row mapping ─────────────────────────────────────────────────

export function mapEntryToFlatRow(entry: ConsoleEntry, fallbackIndex: number): FlatRow | null {
  const commandDetails = entry.CommandDetails ?? {};
  const rawType = String(entry.type ?? '').toLowerCase();
  const sourceValue = commandDetails.saId;
  const destinationValue = commandDetails.daId;
  const tctmType = String(commandDetails.tctmType ?? entry.type ?? '').trim();
  const legacyTestCaseId = String(entry.testCaseId ?? '').trim();
  const testPlanResultId = String(entry.testPlanResultId ?? '').trim() || undefined;
  const testCaseResultId = String(entry.testCaseResultId ?? '').trim() || undefined;
  const testPlanName = String(entry.testPlanName ?? '').trim() || undefined;
  const testCaseName = String(entry.testCaseName ?? '').trim() || undefined;
  const identitySeed =
    testCaseResultId
    ?? legacyTestCaseId
    ?? String(entry.uniqueId ?? entry.CommandUid ?? fallbackIndex);

  return {
    testPlanResultId,
    testCaseId: legacyTestCaseId || String(entry.CommandUid ?? entry.uniqueId ?? fallbackIndex),
    testCaseResultId,
    testPlanName,
    testCaseName,
    rowId: String(entry.CommandUid ?? entry.uniqueId ?? `${identitySeed}-${fallbackIndex}`),
    uniqueEntryId: String(entry.uniqueId ?? entry.CommandUid ?? `${identitySeed}-${fallbackIndex}`),
    entryType: rawType.includes('task') || Boolean(entry.TaskName) ? 'TASK' : 'TC',
    operationId: String(entry.CommandUid ?? entry.uniqueId ?? commandDetails.tctmId ?? `${identitySeed}-${fallbackIndex}`),
    status:
      entry.result !== undefined
        ? entry.result === 'pass' || entry.result === true
          ? 'PASS'
          : entry.result === 'timeout'
          ? 'TIMEOUT'
          : 'FAIL'
        : '',
    commandName: String(entry.TaskName ?? entry.Info ?? commandDetails.tctmType ?? entry.type ?? '—'),
    result: toRowResult(entry),
    timestamp: getTimestampLabel(entry),
    source: sourceValue != null ? String(sourceValue) : undefined,
    destination: destinationValue != null ? String(destinationValue) : undefined,
    tcTmId: commandDetails.tctmId != null ? String(commandDetails.tctmId) : undefined,
    direction: rawType === 'tc' ? 'up' : 'down',
    tctmType,
    entry,
  };
}

// ─── Virtual list section sizing ─────────────────────────────────────────────

export function getSectionSize(group: GroupedSection | undefined): number {
  if (!group) return SECTION_HEADER_HEIGHT + SECTION_GAP;
  if (!group.hasGroupingMeta) return (group.rows.length * ROW_HEIGHT) + SECTION_GAP;
  const rowsHeight = (group.rows.length * ROW_HEIGHT) + SECTION_BORDER_HEIGHT;
  return SECTION_HEADER_HEIGHT + rowsHeight + SECTION_GAP;
}
